import React, { useState } from "react";
import PageLayout from "../components/PageLayout";
import { useTheme } from "../DataContext";
import { USERS, ROLE_LABELS, ROLE_COLORS } from "../auth";

export default function UserManagement() {
  const { theme } = useTheme();
  const [users, setUsers] = useState(USERS.map(u => ({ ...u, active: true })));
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", role: "analyst", name: "", email: "", org: "" });

  const toggleUser = (username) => {
    setUsers(prev => prev.map(u => u.username === username ? { ...u, active: !u.active } : u));
  };

  const addUser = () => {
    if (!form.username || !form.password || !form.name) return;
    setUsers(prev => [...prev, { ...form, created: new Date().toISOString().split("T")[0], active: true }]);
    setForm({ username: "", password: "", role: "analyst", name: "", email: "", org: "" });
    setShowAdd(false);
  };

  return (
    <PageLayout title="User Management" subtitle="Platform accounts, roles, and access control">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px", marginBottom: "20px" }}>
        {Object.entries(ROLE_LABELS).map(([role, label]) => (
          <div key={role} className="pmo-card" style={{ borderLeft: `3px solid ${ROLE_COLORS[role]}` }}>
            <div style={{ fontSize: "10px", color: theme.textMuted, marginBottom: "4px" }}>{label.toUpperCase()}</div>
            <div style={{ fontFamily: "'Orbitron',monospace", fontSize: "20px", color: ROLE_COLORS[role], fontWeight: 700 }}>
              {users.filter(u => u.role === role && u.active).length}
            </div>
          </div>
        ))}
      </div>

      {showAdd && (
        <div className="pmo-card" style={{ marginBottom: "20px", borderColor: theme.accent }}>
          <div className="section-title">Add New User</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px" }}>
            {[
              { l: "Username",     k: "username",  t: "text",     ph: "username" },
              { l: "Password",     k: "password",  t: "password", ph: "••••••••" },
              { l: "Full Name",    k: "name",       t: "text",     ph: "John Smith" },
              { l: "Email",        k: "email",      t: "email",    ph: "user@domain.com" },
              { l: "Organisation", k: "org",        t: "text",     ph: "Department" },
            ].map(f => (
              <div key={f.k}>
                <label style={{ display: "block", fontSize: "10px", color: theme.textMuted, marginBottom: "5px", textTransform: "uppercase", letterSpacing: "0.5px" }}>{f.l}</label>
                <input type={f.t} value={form[f.k]} onChange={e => setForm({ ...form, [f.k]: e.target.value })} placeholder={f.ph} style={{ width: "100%" }} />
              </div>
            ))}
            <div>
              <label style={{ display: "block", fontSize: "10px", color: theme.textMuted, marginBottom: "5px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Role</label>
              <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} style={{ width: "100%" }}>
                {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: "flex", gap: "8px", marginTop: "14px" }}>
            <button onClick={addUser} className="btn-primary">ADD USER</button>
            <button onClick={() => setShowAdd(false)} className="btn-secondary">CANCEL</button>
          </div>
        </div>
      )}

      <div className="pmo-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <div className="section-title" style={{ marginBottom: 0 }}>Platform Users</div>
          <button onClick={() => setShowAdd(true)} className="btn-primary" style={{ padding: "6px 14px", fontSize: "11px" }}>+ ADD USER</button>
        </div>
        <table>
          <thead>
            <tr>{["Username","Full Name","Role","Email","Organisation","Created","Status","Actions"].map(h => <th key={h}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {users.map((u, i) => (
              <tr key={i}>
                <td style={{ fontFamily: "'Share Tech Mono',monospace", color: theme.accent }}>{u.username}</td>
                <td style={{ color: theme.textPrimary, fontWeight: 500 }}>{u.name}</td>
                <td>
                  <span style={{ padding: "2px 10px", borderRadius: "12px", fontSize: "10px", fontWeight: 600, background: `${ROLE_COLORS[u.role]}18`, color: ROLE_COLORS[u.role], border: `1px solid ${ROLE_COLORS[u.role]}33` }}>
                    {ROLE_LABELS[u.role]}
                  </span>
                </td>
                <td style={{ fontSize: "12px" }}>{u.email}</td>
                <td style={{ fontSize: "12px" }}>{u.org}</td>
                <td style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: "11px" }}>{u.created}</td>
                <td>
                  <span style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "10px", fontWeight: 700, background: u.active ? `${theme.success}20` : `${theme.danger}20`, color: u.active ? theme.success : theme.danger }}>
                    {u.active ? "ACTIVE" : "DISABLED"}
                  </span>
                </td>
                <td>
                  <button onClick={() => toggleUser(u.username)} style={{ padding: "3px 8px", borderRadius: "4px", border: `1px solid ${u.active ? theme.danger : theme.success}44`, background: "transparent", color: u.active ? theme.danger : theme.success, fontSize: "10px", cursor: "pointer" }}>
                    {u.active ? "DISABLE" : "ENABLE"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Security Checklist */}
      <div className="pmo-card" style={{ marginTop: "20px", borderColor: theme.warning }}>
        <div className="section-title">Security Deployment Checklist</div>
        {[
          "Change ALL default passwords before production deployment",
          "Enable HTTPS/TLS 1.3 on domain — never serve over HTTP",
          "Migrate from frontend-only auth to backend JWT authentication",
          "Restrict CORS in main.py to your production domain only",
          "Enable rate limiting on all API endpoints",
          "Remove or disable demo/test accounts in production",
          "Enable audit logging for all data access and queries",
          "Verify RBAC — test each role can only access its permitted pages",
        ].map((item, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px", padding: "6px 0", borderBottom: `1px solid ${theme.cardBorder}22` }}>
            <span style={{ color: theme.warning, flexShrink: 0 }}>□</span>
            <span style={{ fontSize: "12px", color: theme.textSecondary }}>{item}</span>
          </div>
        ))}
      </div>
    </PageLayout>
  );
}
